import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Smartphone, Layout, Palette, Rocket, Bell, ShieldCheck, Zap, Sparkles } from "lucide-react";
import WaveBackground from "@/components/site/WaveBackground";
import BundleCTA from "@/components/site/BundleCTA";
import useSEO from "@/lib/useSEO";
import { IMG } from "@/lib/images";

const features = [
  { icon: Palette, title: "UI/UX design", desc: "Interfaces designed for thumbs, not for design awards — clarity, speed, delight." },
  { icon: Layout, title: "Wireframing & prototyping", desc: "Clickable prototypes on your phone before we build, so we agree the flow first." },
  { icon: Smartphone, title: "iOS & Android", desc: "Cross-platform builds with native-feeling performance on both major stores." },
  { icon: Bell, title: "Push notifications", desc: "Bring customers back with well-timed, non-spammy notifications baked in." },
  { icon: Zap, title: "Offline-friendly", desc: "Considered offline states so your app doesn't die on a spotty 4G signal." },
  { icon: ShieldCheck, title: "Secure by default", desc: "Encrypted storage, secure auth, and privacy-first data handling. GDPR-ready." },
];

const included = [
  "Discovery & scoping call",
  "Custom UI/UX design",
  "Interactive prototype (Figma)",
  "iOS + Android build",
  "Backend & database setup",
  "Auth, notifications, analytics",
  "App Store & Play Store listing",
  "Beta testing (TestFlight)",
  "Post-launch support & updates",
];

export default function AppDesign() {
  useSEO({
    title: "App Design — iOS & Android apps for UK businesses | Fenco Digital",
    description: "Bespoke app design: UI/UX, wireframing, interactive prototypes, iOS + Android builds. From discovery to App Store launch.",
    path: "/app-design",
  });

  return (
    <div data-testid="app-design-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <WaveBackground variant="accent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-24">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7 fade-up">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#FF5A1F]">App design</p>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.02] text-gray-900 mt-6">
                Apps customers<br />actually open<span className="text-[#FF5A1F]">.</span>
              </h1>
              <p className="mt-7 text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl">
                Bespoke iOS and Android apps designed around real customer behaviour — bookings, loyalty, ordering or something entirely your own. Clean, fast, and beautifully considered.
              </p>

              <div className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-3.5 max-w-xl" data-testid="app-selling-points">
                {[
                  "Bespoke UI/UX",
                  "Wireframes & prototypes",
                  "iOS + Android builds",
                  "Push notifications",
                  "Analytics & user tracking",
                  "Store submission handled",
                ].map((p) => (
                  <div key={p} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#FF5A1F] shrink-0 mt-0.5" />
                    <span className="text-sm font-medium text-gray-700">{p}</span>
                  </div>
                ))}
              </div>

              <div className="mt-11 flex flex-wrap gap-4">
                <Link
                  to="/contact?interest=App%20Design"
                  data-testid="app-cta-quote"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] rounded-full transition-all duration-300 shadow-lg shadow-orange-500/25 hover:-translate-y-0.5"
                >
                  Get an app quote <ArrowRight size={18} />
                </Link>
                <Link
                  to="/website-design"
                  data-testid="app-cta-web"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-gray-900 bg-white border border-gray-200 hover:border-gray-300 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                >
                  Explore website design
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 flex justify-center lg:justify-end fade-up">
              <div className="relative w-full max-w-md">
                <div className="absolute -inset-10 bg-[#FF5A1F]/10 blur-3xl rounded-full" />
                <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl bg-gray-100">
                  <img
                    src={IMG.appHero}
                    alt="Designer sketching mobile app screens on paper"
                    className="w-full h-full object-cover"
                    data-testid="app-hero-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-28 md:py-36 bg-[#F8F9FA]" data-testid="app-features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#FF5A1F]">What we do</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-5 leading-[1.05]">
              Every detail, considered.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {features.map((f, i) => (
              <div
                key={f.title}
                data-testid={`app-feature-${i}`}
                className="bg-white rounded-2xl p-9 border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-6">
                  <f.icon className="text-[#FF5A1F]" size={22} />
                </div>
                <h3 className="font-display text-xl font-bold text-gray-900">{f.title}</h3>
                <p className="mt-3.5 text-gray-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-28 md:py-36 bg-white" data-testid="app-included-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-5">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-xl">
                  <img src={IMG.appMockup} alt="Designer working on mobile app screens" className="w-full h-full object-cover" loading="lazy" data-testid="app-mockup-image" />
                </div>
                <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-xl mt-10">
                  <img src={IMG.appPhone} alt="Person using a modern mobile app on a phone" className="w-full h-full object-cover" loading="lazy" data-testid="app-phone-image" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#FF5A1F]">What&rsquo;s included</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-5 leading-[1.05]">
                From first sketch to App Store.
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                We handle the whole stack — design, build, backend, store submission and post-launch iteration. One partner, no juggling.
              </p>
              <ul className="mt-9 grid sm:grid-cols-2 gap-x-8 gap-y-3.5">
                {included.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-gray-700">
                    <CheckCircle2 size={18} className="text-[#FF5A1F] shrink-0 mt-0.5" />
                    <span className="font-medium">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-28 md:py-36 bg-[#0a0a0a] text-white relative overflow-hidden" data-testid="app-process-section">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FF5A1F] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#FF5A1F]">Our process</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-5 leading-[1.05]">
              Discovery → design → build → launch.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              ["01", "Discovery", "We map your users, features and priorities before drawing a single screen."],
              ["02", "Design", "Wireframes, then high-fidelity prototypes you can tap and feel on your phone."],
              ["03", "Build", "Native-feeling cross-platform build with backend, auth and analytics from day one."],
              ["04", "Launch", "TestFlight beta, App Store & Play Store submission, and post-launch support."],
            ].map(([n, t, d], i) => (
              <div key={n} data-testid={`app-step-${i}`} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500">
                <p className="font-display text-4xl font-extrabold text-[#FF5A1F]">{n}</p>
                <h3 className="font-display text-lg font-bold mt-5">{t}</h3>
                <p className="text-gray-300 mt-2.5 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUNDLE CROSS-SELL */}
      <BundleCTA />

      {/* FINAL CTA */}
      <section className="py-28 md:py-36 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Sparkles className="mx-auto text-[#FF5A1F] mb-7" size={34} strokeWidth={1.5} />
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
            Got an app idea<span className="text-[#FF5A1F]">?</span>
          </h2>
          <p className="mt-7 text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Tell us what you&rsquo;re trying to build and we&rsquo;ll come back with a scope and quote within one working day.
          </p>
          <Link
            to="/contact?interest=App%20Design"
            data-testid="app-final-cta"
            className="mt-11 inline-flex items-center gap-2 px-9 py-4 text-base font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] rounded-full transition-all duration-300 hover:-translate-y-0.5 shadow-xl shadow-orange-500/25"
          >
            Start a project <Rocket size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
